const groupPrefixes = {
  Bikes: 'bicicletas',
  Gloves: 'luvas',
  Helmets: 'capacetes',
  'Helmets MIPS': 'capacetes-mips',
  Shoes: 'sapatilhas',
};

const chartAliases = {
  Sapatilhas: ['sapatilhas', 'Sapatilhas'],
  "Men's Gloves (Alpha Range)": ['luvas-masculinas', 'Luvas masculinas'],
  "Women's Gloves (Alpha Range)": ['luvas-femininas', 'Luvas femininas'],
  'MIPS Helmets': ['capacetes-mips-padrao', 'Capacetes MIPS'],
  'Capacetes MIPS — S/M e L/XL': ['capacetes-mips-sm-lxl', 'Capacetes MIPS — S/M e L/XL'],
  'Asia Fit (Ranged Sizing)': ['capacetes-mips-asia-fit', 'Capacetes — Asia Fit'],
  'MIPS Asia Fit (Ranged Sizing)': ['capacetes-mips-asia-fit-sm-lxl', 'Capacetes MIPS — Asia Fit'],
};

const headers = {
  height: 'Altura',
  size: 'Tamanho',
  Reach: 'Reach (alcance)',
  'head circumference': 'Circunferência da cabeça',
  'middle finger length': 'Comprimento do dedo médio',
  'palm length': 'Comprimento da palma',
  'thumb length': 'Comprimento do polegar',
  'palm circumference': 'Circunferência da palma',
  measure: 'Comprimento do pé',
  EURO: 'EU',
  USA: 'US',
  UK: 'UK',
  BRASIL: 'BR',
};

function slug(value) {
  return String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function sizeChartKey(group, chart) {
  if (chartAliases[chart.title]) return chartAliases[chart.title][0];
  return `${groupPrefixes[group]}-${slug(chart.title.replace(/ Helmet/g, ''))}`;
}

export function sizeChartTitle(chart) {
  return chartAliases[chart.title]?.[1] || chart.title.replace(/ Helmet/g, '');
}

export function resolveSizeCharts(data, requested) {
  const keys = [...new Set((Array.isArray(requested) ? requested : [requested])
    .filter((key) => typeof key === 'string' && key.trim()).map((key) => {
      const trimmed = key.trim();
      // Compatibilidade com os códigos cadastrados antes da tabela única.
      return ['sapatilhas-masculinas', 'sapatilhas-femininas'].includes(trimmed) ? 'sapatilhas' : trimmed;
    }))];
  const index = new Map();
  for (const [group, charts] of Object.entries(data.groups || {})) {
    if (!groupPrefixes[group]) continue;
    for (const chart of charts) index.set(sizeChartKey(group, chart), { ...chart, group });
  }
  if (data.unit !== 'metric' || !keys.length || keys.some((key) => !index.has(key))) {
    const error = new Error('Tabela não configurada ou código desconhecido.');
    error.name = 'SizeGuideConfigurationError';
    throw error;
  }
  return keys.map((key) => index.get(key));
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function renderChart(chart) {
  const article = element('section', 'mb-product-size-guide__chart');
  const heading = element('h3', '', sizeChartTitle(chart));
  const scroll = element('div', 'mb-product-size-guide__scroll');
  scroll.tabIndex = 0;
  scroll.setAttribute('role', 'region');
  scroll.setAttribute('aria-label', `Tabela de ${sizeChartTitle(chart)} — role horizontalmente se necessário`);
  const table = element('table', 'mb-product-size-guide__table');
  table.setAttribute('aria-label', sizeChartTitle(chart));
  const head = element('thead');
  const headerRow = element('tr');
  chart.headers.forEach((header) => {
    const unit = header.unit && header.unit !== 'None' ? ` (${header.unit})` : '';
    const cell = element('th', '', `${headers[header.value] || header.value}${unit}`);
    cell.scope = 'col';
    headerRow.append(cell);
  });
  head.append(headerRow);
  const body = element('tbody');
  chart.rows.forEach((values) => {
    const row = element('tr');
    values.forEach((value, i) => {
      const cell = element(i === 0 ? 'th' : 'td', '', String(value).replace(/(\d)\.(\d)/g, '$1,$2'));
      if (i === 0) cell.scope = 'row';
      row.append(cell);
    });
    body.append(row);
  });
  table.append(head, body);
  scroll.append(table);
  article.append(heading, scroll);
  return article;
}

function initializeProductSizeGuide() {
  const cache = new Map();
  let dialog, productLabel, content, footerLink, closeButton, opener;
  let requestId = 0;

  function createDialog() {
    dialog = element('dialog', 'mb-product-size-guide');
    dialog.setAttribute('aria-labelledby', 'MbProductSizeGuideTitle');
    const header = element('header', 'mb-product-size-guide__header');
    const titleContainer = element('div');
    const title = element('h2', '', 'Guia de tamanhos');
    title.id = 'MbProductSizeGuideTitle';
    productLabel = element('p', 'mb-product-size-guide__product');
    titleContainer.append(title, productLabel);
    closeButton = element('button', 'mb-product-size-guide__close', '×');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Fechar guia de tamanhos');
    closeButton.addEventListener('click', () => dialog.close());
    header.append(titleContainer, closeButton);
    const body = element('div', 'mb-product-size-guide__body');
    content = element('div');
    const footer = element('p', 'mb-product-size-guide__footer');
    footerLink = element('a', '', 'Ver guia de tamanhos completo');
    footer.append(footerLink);
    body.append(content, footer);
    dialog.append(header, body);
    dialog.addEventListener('close', () => {
      requestId += 1;
      document.documentElement.classList.remove('mb-product-size-guide-open');
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right ||
          event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    document.body.append(dialog);
  }

  function loadData(url) {
    if (!cache.has(url)) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      const pending = fetch(url, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Não foi possível carregar as tabelas.');
          return response.json();
        })
        .catch((error) => { cache.delete(url); throw error; })
        .finally(() => window.clearTimeout(timeout));
      cache.set(url, pending);
    }
    return cache.get(url);
  }

  async function show(trigger) {
    if (!dialog) createDialog();
    const currentRequest = ++requestId;
    opener = trigger;
    productLabel.textContent = trigger.dataset.sizeGuideProduct || '';
    footerLink.href = trigger.href;
    const status = element('p', 'mb-product-size-guide__status', 'Carregando tabela de tamanhos…');
    status.setAttribute('role', 'status');
    content.replaceChildren(status);
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('mb-product-size-guide-open');
    dialog.scrollTop = 0;
    closeButton.focus({ preventScroll: true });
    try {
      const requested = JSON.parse(trigger.dataset.sizeGuideKeys || '[]');
      const data = await loadData(trigger.dataset.sizeGuideSource);
      if (currentRequest !== requestId || !dialog.open) return;
      const charts = resolveSizeCharts(data, requested);
      const description = charts.some((chart) => chart.group === 'Shoes')
        ? 'Comprimento do pé em centímetros. BR, EU e UK indicam as numerações equivalentes.'
        : 'Medidas no sistema métrico. Confira a unidade indicada em cada coluna.';
      content.replaceChildren(element('p', 'mb-product-size-guide__units', description), ...charts.map(renderChart));
    } catch (error) {
      if (currentRequest !== requestId || !dialog.open) return;
      const message = element('p', 'mb-product-size-guide__status',
        error.name === 'SizeGuideConfigurationError'
          ? 'A tabela deste produto ainda não está disponível. Consulte o guia completo abaixo.'
          : 'Não foi possível carregar a tabela. Tente novamente ou consulte o guia completo abaixo.');
      message.setAttribute('role', 'alert');
      content.replaceChildren(message);
      if (error.name !== 'SizeGuideConfigurationError') {
        const retry = element('button', 'mb-product-size-guide__retry', 'Tentar novamente');
        retry.type = 'button';
        retry.addEventListener('click', () => show(trigger));
        content.append(retry);
      }
    }
  }

  // Delegação mantém o link funcional após a atualização de variantes pelo tema.
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-mb-product-size-guide]') : null;
    if (!trigger || event.defaultPrevented || event.button !== 0 ||
        event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    if (typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
    event.preventDefault();
    show(trigger);
  });
}

if (typeof document !== 'undefined') initializeProductSizeGuide();
